import { useEffect, useState } from "react";
import { Plus, Search, Users, Pencil, Trash2, Mail, Phone, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { PageHeader } from "../components/ui/PageHeader";
import { Select } from "../components/ui/Select";
import api from "../lib/api";
import { ContactFormModal, contactTypeLabel, type Contact } from "../components/contacts/ContactFormModal";

export default function Contacts() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = () => api.get("/api/contacts").then((res) => setContacts(res.data));

  const openNew = () => {
    setEditing(null);
    setIsOpen(true);
  };

  const openEdit = (c: Contact) => {
    setEditing(c);
    setIsOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Weet je zeker dat je dit contact wilt verwijderen?")) return;
    await api.delete(`/api/contacts/${id}`);
    loadContacts();
  };

  const filtered = contacts.filter((c) => {
    const q = search.toLowerCase();
    const matchesSearch =
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q);
    const matchesType = typeFilter ? c.type === typeFilter : true;
    return matchesSearch && matchesType;
  });

  const typeOptions = [
    { value: "", label: "Alle types" },
    { value: "CUSTOMER", label: "Klant" },
    { value: "SUPPLIER", label: "Leverancier" },
    { value: "COMPANY", label: "Bedrijf" },
  ];

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Contacten"
        subtitle="Klanten en leveranciers"
        action={
          <Button onClick={openNew}>
            <Plus size={27} /> Nieuw contact
          </Button>
        }
      />

      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="search-chip flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={27} />
            <input
              type="text"
              placeholder="Zoek contacten..."
              className="w-full bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select
            options={typeOptions}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-full md:w-48"
          />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <Users size={42} className="text-slate-400 mb-2" />
          Geen contacten gevonden.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card className="p-5 surface-card">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-400 to-indigo-500 flex items-center justify-center text-white font-bold shadow-lg shadow-brand-500/20">
                    {(c.name?.[0] || "?").toUpperCase()}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[0.625rem] font-bold px-2 py-1 rounded-full bg-slate-100 text-slate-600 uppercase tracking-wide">
                      {contactTypeLabel(c.type)}
                    </span>
                    <button
                      onClick={() => openEdit(c)}
                      className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors"
                      title="Bewerken"
                    >
                      <Pencil size={24} />
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="p-2 rounded-xl hover:bg-rose-50 text-slate-500 hover:text-rose-600 transition-colors"
                      title="Verwijderen"
                    >
                      <Trash2 size={24} />
                    </button>
                  </div>
                </div>
                <h3 className="font-bold text-lg text-slate-800">{c.name}</h3>
                <div className="mt-3 space-y-2 text-sm text-slate-500">
                  {c.email && (
                    <div className="flex items-center gap-2">
                      <Mail size={21} /> {c.email}
                    </div>
                  )}
                  {c.phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={21} /> {c.phone}
                    </div>
                  )}
                  {(c.city || c.country) && (
                    <div className="flex items-center gap-2">
                      <MapPin size={21} /> {[c.address, c.zipCode, c.city, c.country].filter(Boolean).join(", ")}
                    </div>
                  )}
                </div>
                {(c.kvkNumber || c.vatNumber) && (
                  <div className="mt-4 pt-4 border-t border-slate-100 flex gap-3 text-xs text-slate-500">
                    {c.kvkNumber && <span>KVK: {c.kvkNumber}</span>}
                    {c.vatNumber && <span>BTW: {c.vatNumber}</span>}
                  </div>
                )}
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      <ContactFormModal
        isOpen={isOpen}
        contact={editing}
        onClose={() => setIsOpen(false)}
        onSaved={() => {
          setIsOpen(false);
          setEditing(null);
          loadContacts();
        }}
      />
    </div>
  );
}
